import {
    CalendarDays,
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

type PersonalInfoDatePickerProps = {
    id: string;
    value: string;
    placeholder: string;
    maxDate?: Date;
    minDate?: Date;
    onChange: (value: string) => void;
};

const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const WEEKDAYS = [
    "Su",
    "Mo",
    "Tu",
    "We",
    "Th",
    "Fr",
    "Sa",
];

const pad =
    (
        value: number
    ) =>
        String(
            value
        ).padStart(
            2,
            "0"
        );

const toDateValue =
    (
        date: Date
    ) =>
        `${date.getFullYear()}-${pad(
            date.getMonth() +
            1
        )}-${pad(
            date.getDate()
        )}`;

const parseDateValue =
    (
        value: string
    ) => {
        if (
            !value
        ) {
            return null;
        }

        const [
            year,
            month,
            day,
        ] =
            value
                .split(
                    "-"
                )
                .map(
                    Number
                );

        if (
            !year ||
            !month ||
            !day
        ) {
            return null;
        }

        return new Date(
            year,
            month -
            1,
            day
        );
    };

const formatDisplayDate =
    (
        value: string
    ) => {
        const parsed =
            parseDateValue(
                value
            );

        if (
            !parsed
        ) {
            return "";
        }

        return new Intl.DateTimeFormat(
            "en-IN",
            {
                day:
                    "2-digit",
                month:
                    "short",
                year:
                    "numeric",
            }
        ).format(
            parsed
        );
    };

const isSameDate =
    (
        first: Date,
        second: Date
    ) =>
        first.getFullYear() ===
        second.getFullYear() &&
        first.getMonth() ===
        second.getMonth() &&
        first.getDate() ===
        second.getDate();

const PersonalInfoDatePicker = ({
    id,
    value,
    placeholder,
    maxDate,
    minDate,
    onChange,
}: PersonalInfoDatePickerProps) => {
    const wrapperRef =
        useRef<HTMLDivElement>(
            null
        );

    const selectedDate =
        parseDateValue(
            value
        );

    const fallbackDate =
        selectedDate ??
        maxDate ??
        new Date();

    const [
        open,
        setOpen,
    ] =
        useState(false);

    const [
        viewDate,
        setViewDate,
    ] =
        useState(
            new Date(
                fallbackDate.getFullYear(),
                fallbackDate.getMonth(),
                1
            )
        );

    const [
        monthOpen,
        setMonthOpen,
    ] =
        useState(false);

    const [
        yearOpen,
        setYearOpen,
    ] =
        useState(false);

    useEffect(
        () => {
            if (
                !open
            ) {
                return;
            }

            const next =
                selectedDate ??
                maxDate ??
                new Date();

            setViewDate(
                new Date(
                    next.getFullYear(),
                    next.getMonth(),
                    1
                )
            );
        },
        [
            open,
            value,
            maxDate,
        ]
    );

    useEffect(
        () => {
            const handleOutside =
                (
                    event: MouseEvent
                ) => {
                    if (
                        wrapperRef.current &&
                        !wrapperRef.current.contains(
                            event.target as Node
                        )
                    ) {
                        setOpen(
                            false
                        );
                        setMonthOpen(
                            false
                        );
                        setYearOpen(
                            false
                        );
                    }
                };

            const handleEscape =
                (
                    event: KeyboardEvent
                ) => {
                    if (
                        event.key ===
                        "Escape"
                    ) {
                        setOpen(
                            false
                        );
                        setMonthOpen(
                            false
                        );
                        setYearOpen(
                            false
                        );
                    }
                };

            document.addEventListener(
                "mousedown",
                handleOutside
            );

            window.addEventListener(
                "keydown",
                handleEscape
            );

            return () => {
                document.removeEventListener(
                    "mousedown",
                    handleOutside
                );

                window.removeEventListener(
                    "keydown",
                    handleEscape
                );
            };
        },
        []
    );

    const yearOptions =
        useMemo(
            () => {
                const maximumYear =
                    (
                        maxDate ??
                        new Date()
                    ).getFullYear();

                const minimumYear =
                    (
                        minDate ??
                        new Date(
                            maximumYear -
                            100,
                            0,
                            1
                        )
                    ).getFullYear();

                const years: number[] =
                    [];

                for (
                    let year =
                        maximumYear;
                    year >=
                    minimumYear;
                    year -=
                    1
                ) {
                    years.push(
                        year
                    );
                }

                return years;
            },
            [
                maxDate,
                minDate,
            ]
        );

    const calendarDays =
        useMemo(
            () => {
                const year =
                    viewDate.getFullYear();

                const month =
                    viewDate.getMonth();

                const firstDay =
                    new Date(
                        year,
                        month,
                        1
                    ).getDay();

                const daysInMonth =
                    new Date(
                        year,
                        month +
                        1,
                        0
                    ).getDate();

                const cells:
                    Array<
                        Date | null
                    > =
                    [];

                for (
                    let index =
                        0;
                    index <
                    firstDay;
                    index +=
                    1
                ) {
                    cells.push(
                        null
                    );
                }

                for (
                    let day =
                        1;
                    day <=
                    daysInMonth;
                    day +=
                    1
                ) {
                    cells.push(
                        new Date(
                            year,
                            month,
                            day
                        )
                    );
                }

                while (
                    cells.length %
                    7 !==
                    0
                ) {
                    cells.push(
                        null
                    );
                }

                return cells;
            },
            [
                viewDate,
            ]
        );

    const dateDisabled =
        (
            date: Date
        ) => {
            if (
                maxDate &&
                date >
                new Date(
                    maxDate.getFullYear(),
                    maxDate.getMonth(),
                    maxDate.getDate(),
                    23,
                    59,
                    59
                )
            ) {
                return true;
            }

            if (
                minDate &&
                date <
                new Date(
                    minDate.getFullYear(),
                    minDate.getMonth(),
                    minDate.getDate()
                )
            ) {
                return true;
            }

            return false;
        };

    const shiftMonth =
        (
            direction: number
        ) => {
            setViewDate(
                (
                    previous
                ) =>
                    new Date(
                        previous.getFullYear(),
                        previous.getMonth() +
                        direction,
                        1
                    )
            );

            setMonthOpen(
                false
            );
            setYearOpen(
                false
            );
        };

    return (
        <div
            className="personal-info-date-picker"
            ref={
                wrapperRef
            }
        >
            <button
                id={
                    id
                }
                type="button"
                className={`personal-info-date-trigger ${open
                    ? "open"
                    : ""
                    }`}
                onClick={() =>
                    setOpen(
                        (
                            previous
                        ) =>
                            !previous
                    )
                }
                aria-expanded={
                    open
                }
            >
                <span
                    className={
                        value
                            ? "select-value"
                            : "select-placeholder"
                    }
                >
                    {value
                        ? formatDisplayDate(
                            value
                        )
                        : placeholder}
                </span>

                <CalendarDays
                    size={
                        16
                    }
                    strokeWidth={
                        1.9
                    }
                />
            </button>

            {open && (
                <div className="personal-info-calendar">
                    <div className="personal-info-calendar-head">
                        <button
                            type="button"
                            className="calendar-arrow"
                            onClick={() =>
                                shiftMonth(
                                    -1
                                )
                            }
                            aria-label="Previous month"
                        >
                            <ChevronLeft
                                size={
                                    17
                                }
                            />
                        </button>

                        <div className="calendar-period">
                            <div className="calendar-period-control">
                                <button
                                    type="button"
                                    className="calendar-period-button"
                                    onClick={() => {
                                        setMonthOpen(
                                            (
                                                previous
                                            ) =>
                                                !previous
                                        );

                                        setYearOpen(
                                            false
                                        );
                                    }}
                                >
                                    {
                                        MONTHS[
                                        viewDate.getMonth()
                                        ]
                                    }

                                    <ChevronDown
                                        size={
                                            13
                                        }
                                        className={
                                            monthOpen
                                                ? "rotate"
                                                : ""
                                        }
                                    />
                                </button>

                                {monthOpen && (
                                    <div className="calendar-mini-menu month-menu">
                                        {MONTHS.map(
                                            (
                                                month,
                                                index
                                            ) => (
                                                <button
                                                    type="button"
                                                    key={
                                                        month
                                                    }
                                                    className={
                                                        viewDate.getMonth() ===
                                                            index
                                                            ? "selected"
                                                            : ""
                                                    }
                                                    onClick={() => {
                                                        setViewDate(
                                                            new Date(
                                                                viewDate.getFullYear(),
                                                                index,
                                                                1
                                                            )
                                                        );

                                                        setMonthOpen(
                                                            false
                                                        );
                                                    }}
                                                >
                                                    <span>
                                                        {
                                                            month
                                                        }
                                                    </span>

                                                    {viewDate.getMonth() ===
                                                        index && (
                                                            <Check
                                                                size={
                                                                    13
                                                                }
                                                            />
                                                        )}
                                                </button>
                                            )
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="calendar-period-control">
                                <button
                                    type="button"
                                    className="calendar-period-button"
                                    onClick={() => {
                                        setYearOpen(
                                            (
                                                previous
                                            ) =>
                                                !previous
                                        );

                                        setMonthOpen(
                                            false
                                        );
                                    }}
                                >
                                    {
                                        viewDate.getFullYear()
                                    }

                                    <ChevronDown
                                        size={
                                            13
                                        }
                                        className={
                                            yearOpen
                                                ? "rotate"
                                                : ""
                                        }
                                    />
                                </button>

                                {yearOpen && (
                                    <div className="calendar-mini-menu year-menu">
                                        {yearOptions.map(
                                            (
                                                year
                                            ) => (
                                                <button
                                                    type="button"
                                                    key={
                                                        year
                                                    }
                                                    className={
                                                        viewDate.getFullYear() ===
                                                            year
                                                            ? "selected"
                                                            : ""
                                                    }
                                                    onClick={() => {
                                                        setViewDate(
                                                            new Date(
                                                                year,
                                                                viewDate.getMonth(),
                                                                1
                                                            )
                                                        );

                                                        setYearOpen(
                                                            false
                                                        );
                                                    }}
                                                >
                                                    <span>
                                                        {
                                                            year
                                                        }
                                                    </span>

                                                    {viewDate.getFullYear() ===
                                                        year && (
                                                            <Check
                                                                size={
                                                                    13
                                                                }
                                                            />
                                                        )}
                                                </button>
                                            )
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <button
                            type="button"
                            className="calendar-arrow"
                            onClick={() =>
                                shiftMonth(
                                    1
                                )
                            }
                            aria-label="Next month"
                        >
                            <ChevronRight
                                size={
                                    17
                                }
                            />
                        </button>
                    </div>

                    <div className="personal-info-weekdays">
                        {WEEKDAYS.map(
                            (
                                day
                            ) => (
                                <span
                                    key={
                                        day
                                    }
                                >
                                    {
                                        day
                                    }
                                </span>
                            )
                        )}
                    </div>

                    <div className="personal-info-calendar-grid">
                        {calendarDays.map(
                            (
                                date,
                                index
                            ) => {
                                if (
                                    !date
                                ) {
                                    return (
                                        <span
                                            className="calendar-empty-day"
                                            key={`empty-${index}`}
                                        />
                                    );
                                }

                                const disabled =
                                    dateDisabled(
                                        date
                                    );

                                const selected =
                                    selectedDate
                                        ? isSameDate(
                                            date,
                                            selectedDate
                                        )
                                        : false;

                                const today =
                                    isSameDate(
                                        date,
                                        new Date()
                                    );

                                return (
                                    <button
                                        type="button"
                                        className={`calendar-day ${selected
                                            ? "selected"
                                            : ""
                                            } ${today
                                                ? "today"
                                                : ""
                                            }`}
                                        key={
                                            toDateValue(
                                                date
                                            )
                                        }
                                        disabled={
                                            disabled
                                        }
                                        onClick={() => {
                                            onChange(
                                                toDateValue(
                                                    date
                                                )
                                            );

                                            setOpen(
                                                false
                                            );
                                        }}
                                    >
                                        {
                                            date.getDate()
                                        }
                                    </button>
                                );
                            }
                        )}
                    </div>

                    <div className="personal-info-calendar-footer">
                        <button
                            type="button"
                            onClick={() =>
                                onChange(
                                    ""
                                )
                            }
                        >
                            Clear
                        </button>

                        <button
                            type="button"
                            className="primary"
                            disabled={
                                dateDisabled(
                                    new Date()
                                )
                            }
                            onClick={() => {
                                const today =
                                    maxDate &&
                                        maxDate <
                                        new Date()
                                        ? maxDate
                                        : new Date();

                                onChange(
                                    toDateValue(
                                        today
                                    )
                                );

                                setOpen(
                                    false
                                );
                            }}
                        >
                            Today
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PersonalInfoDatePicker;
